import styled from '@emotion/styled';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { MatchDetail } from '../api/leaderboard';
import sportVideoPosterUrl from '../images/sport-video-poster.png';

type Props = {
  match: MatchDetail;
  videoUrl: string;
  player1PhotoUrl?: string;
  player2PhotoUrl?: string;
  onVideoEnd?: () => void;
  className?: string;
};

const MatchReplay = ({
  match: { datetime, player1, player2 },
  videoUrl,
  player1PhotoUrl,
  player2PhotoUrl,
  onVideoEnd = () => {},
  className,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    videoRef.current?.load();

    const onLoad = () => {
      setIsVideoLoaded(true);
      videoRef.current?.removeEventListener('loadeddata', onLoad);
    };

    videoRef.current?.addEventListener('loadeddata', onLoad);
  }, [videoUrl]);

  return (
    <Container className={className}>
      <Header
        variants={{
          hidden: {
            opacity: 0,
            transition: {
              delayChildren: 0.5,
            },
          },
          visible: { opacity: 1 },
        }}
        initial="hidden"
        animate={isVideoLoaded ? 'visible' : 'hidden'}
      >
        <DateTime
          variants={{
            hidden: { opacity: 0, y: '-200%' },
            visible: { opacity: 1, y: 0 },
          }}
        >
          {format(datetime, 'MMMM d, yyyy, h:mm aaa')}
        </DateTime>
        <ScoreBoard>
          <ScoreBoardPlayer
            variants={{
              hidden: { opacity: 0, x: '-25%' },
              visible: { opacity: 1, x: 0 },
            }}
          >
            <ScoreBoardPlayerName>{player1.name}</ScoreBoardPlayerName>
            <ScoreBoardPlayerImageSlot>
              <ScoreBoardPlayerImageContainer>
                {player1PhotoUrl && (
                  <motion.img
                    alt={player1.name}
                    src={player1PhotoUrl}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  />
                )}
              </ScoreBoardPlayerImageContainer>
            </ScoreBoardPlayerImageSlot>
            <ScoreBoardPlayerScore>{player1.score}</ScoreBoardPlayerScore>
          </ScoreBoardPlayer>
          <ScoreBoardDivider
            variants={{
              hidden: { opacity: 0, y: '100%' },
              visible: { opacity: 1, y: 0 },
            }}
          />
          <ScoreBoardPlayer
            variants={{
              hidden: { opacity: 0, x: '25%' },
              visible: { opacity: 1, x: 0 },
            }}
          >
            <ScoreBoardPlayerScore>{player2.score}</ScoreBoardPlayerScore>
            <ScoreBoardPlayerImageSlot>
              <ScoreBoardPlayerImageContainer>
                {player2PhotoUrl && (
                  <motion.img
                    alt={player2.name}
                    src={player2PhotoUrl}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  />
                )}
              </ScoreBoardPlayerImageContainer>
            </ScoreBoardPlayerImageSlot>
            <ScoreBoardPlayerName>{player2.name}</ScoreBoardPlayerName>
          </ScoreBoardPlayer>
        </ScoreBoard>
      </Header>
      <VideoContainer
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }}
        initial="hidden"
        animate={isVideoLoaded ? 'visible' : 'hidden'}
      >
        <video
          ref={videoRef}
          poster={sportVideoPosterUrl}
          preload="auto"
          playsInline
          autoPlay
          disablePictureInPicture
          muted
          onEnded={() => {
            setIsVideoLoaded(false);
            setTimeout(() => onVideoEnd(), 500);
          }}
        >
          <source
            src={videoUrl}
            title={`${player1.name} vs. ${player2.name} replay video`}
            type="video/mp4"
          />
        </video>
      </VideoContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 1rem;
  overflow: hidden;
  width: 100%;
  background-color: #000000;
  color: #eaeaea;
  font-family: 'Nunito', serif;
`;

const Header = styled(motion.div)`
  display: flex;
  flex-direction: column;
  background-color: #343434;
  width: 100%;
  padding: 1.5rem 2rem;
  overflow: hidden;
  z-index: 1;
  border-radius: 1rem 1rem 0 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2rem 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 2.5rem 5rem;
  }

  h1,
  h3 {
    margin: 0;
  }
`;

const DateTime = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  font-size: 0.825rem;
  font-weight: 400;
  margin-bottom: 1rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 1.25rem;
    margin-bottom: 2rem;
  }
`;

const ScoreBoard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    justify-content: center;
  }
`;

const ScoreBoardPlayer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 20rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: inherit;
  }
`;

const ScoreBoardPlayerScore = styled.h1`
  font-family: 'Lato', serif;
  font-size: 1.5rem;
  order: 3;
  flex: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    order: inherit;
    font-size: 3.5rem;
  }

  @media (min-width: 1250px) {
    font-size: 5.25rem;
  }
`;

const ScoreBoardPlayerName = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  order: 2;
  flex: 6;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.5rem;
    order: inherit;
    flex: 1;
  }

  @media (min-width: 1250px) {
    font-size: 2.75rem;
  }
`;

const ScoreBoardPlayerImageSlot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  order: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    order: inherit;
    flex: 6;
  }
`;

const ScoreBoardPlayerImageContainer = styled(motion.div)`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 1.25rem;
  border: 0.125rem solid #ffffff;
  background-color: #ddd;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 4rem;
    height: 4rem;
    border-radius: 2rem;
  }

  @media (min-width: 1250px) {
    width: 6rem;
    height: 6rem;
    border-radius: 3rem;
  }
`;

const ScoreBoardDivider = styled(motion.h1)`
  display: flex;
  align-items: center;
  justify-content: center;

  ::after {
    content: 'vs';
    margin: 0.25rem 0.5rem;
    font-weight: 300;
    font-size: 1.25rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      content: ':';
      margin: 0 1rem;
      font-weight: 800;
      font-size: 3.5rem;
    }

    @media (min-width: 1250px) {
      font-size: 5.25rem;
    }
  }
`;

const VideoContainer = styled(motion.div)`
  width: 100%;
  background-color: #000000;
  display: flex;
  justify-content: center;
  align-items: center;

  video {
    width: 100%;
    background-color: #ddd;
    border-radius: 0 0 1rem 1rem;
    overflow: hidden;
    transform: translateZ(0);
  }
`;

export default MatchReplay;
